import dayjs from 'dayjs';
import { BYTE_TO_KILOBYTE, BYTE_TO_MEGABYTE, DEFAULT_VALUE_0, DEFAULT_VALUE_1, PRICE_PATTERN } from 'common/config';

export const countFormatter = (value?: number) => `${value} шт`;

export const phoneFormatter = (value?: string) => {
  if (!value) {
    return '';
  }

  const digits = value.replace(/[^\d+]/g, '');
  const sanitized = digits.startsWith('+') ? digits : `+${digits}`;
  const onlyDigits = sanitized.replace(/(?!^\+)\D/g, '');
  const match = onlyDigits.match(/^(\+?)(\d?)(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})$/);

  if (!match) {
    return onlyDigits;
  }

  const [, plus, country, part1, part2, part3, part4] = match;
  let formatted = `${plus}${country}`;

  if (part1) {
    formatted += ` (${part1}`;
  }
  if (part1 && part2) {
    formatted += `) ${part2}`;
  }
  if (part2 && part3) {
    formatted += `-${part3}`;
  }
  if (part3 && part4) {
    formatted += `-${part4}`;
  }

  return formatted;
};

export const priceFormatter = (price: number = 0) => `${price.toFixed(2).replace(PRICE_PATTERN, ' ')} ₽`;

export const positionFormatter = (count: number) => {
  const countArray = Array.from(String(count)).map((num) => Number(num));
  const lastDigit = countArray[countArray.length - 1];
  const penultimateDigit = countArray[countArray.length - 2];

  switch (true) {
    case lastDigit === 1 && lastDigit === penultimateDigit:
    case lastDigit > 1 && lastDigit < 5 && penultimateDigit === 1:
    case lastDigit > 4 && lastDigit < 10:
    case lastDigit === 0: {
      /*
      1. Numbers including 11
      2. Numbers including 12, 13, 14
      3. Numbers ending in 5, 6, 7, 8, 9
      4. Numbers ending in 0
      */
      return `${count} позиций`;
    }
    case lastDigit > 1 && lastDigit < 5 && penultimateDigit !== 1: {
      // Numbers ending in 2, 3, 4, excluding numbers including 12, 13, 14
      return `${count} позиции`;
    }
    default: {
      // Numbers ending in 1, excluding numbers including 11
      return `${count} позиция`;
    }
  }
};

export const discountFormatter = (value?: number) => `${value}%`;

export const deliveryTimeFormatter = {
  // Дней до первого доступного дня в календаре доставки — по бакету подписи (full),
  // а не по сырому сроку: «72 часа» → +3, «1 неделя» → +7, дальше — меньшая граница подписи
  // («2–3 недели» → 14, «1 месяц» → 30, «2–3 месяца» → 60, «4–6 месяцев» → 120).
  // null — «Доступно под заказ»: дата не выбирается.
  bucketDays: (deliveryInDays: number): number | null => {
    switch (true) {
      case deliveryInDays === 1: {
        return 1;
      }
      case deliveryInDays > 1 && deliveryInDays <= 4: {
        return 3;
      }
      case deliveryInDays > 4 && deliveryInDays <= 7: {
        return 7;
      }
      case deliveryInDays > 7 && deliveryInDays <= 21: {
        return 14;
      }
      case deliveryInDays > 21 && deliveryInDays <= 30: {
        return 30;
      }
      case deliveryInDays > 30 && deliveryInDays <= 90: {
        return 60;
      }
      case deliveryInDays > 90 && deliveryInDays <= 120: {
        return 120;
      }
      default: {
        return null;
      }
    }
  },
  full: (deliveryInDays: number) => {
    switch (true) {
      case deliveryInDays === 1: {
        return '24 часа';
      }
      case deliveryInDays > 1 && deliveryInDays <= 4: {
        return '72 часа';
      }
      case deliveryInDays > 4 && deliveryInDays <= 7: {
        return '1 неделя';
      }
      case deliveryInDays > 7 && deliveryInDays <= 21: {
        return '2–3 недели';
      }
      case deliveryInDays > 21 && deliveryInDays <= 30: {
        return '1 месяц';
      }
      case deliveryInDays > 30 && deliveryInDays <= 90: {
        return '2–3 месяца';
      }
      case deliveryInDays > 90 && deliveryInDays <= 120: {
        return '4–6 месяцев';
      }
      default: {
        return 'Доступно под заказ';
      }
    }
  },
  short: (deliveryInDays?: number) => {
    if (!deliveryInDays) {
      return 'Уточнить';
    }

    switch (true) {
      case deliveryInDays === 1: {
        return '24 ч.';
      }
      case deliveryInDays > 1 && deliveryInDays <= 4: {
        return '72 ч.';
      }
      case deliveryInDays > 4 && deliveryInDays <= 7: {
        return '1 нед.';
      }
      case deliveryInDays > 7 && deliveryInDays <= 21: {
        return '2–3 нед.';
      }
      case deliveryInDays > 21 && deliveryInDays <= 30: {
        return '1 мес.';
      }
      case deliveryInDays > 30 && deliveryInDays <= 90: {
        return '2–3 мес.';
      }
      case deliveryInDays > 90 && deliveryInDays <= 120: {
        return '4–6 месяцев';
      }
      default: {
        return 'Уточнить';
      }
    }
  },
};

export const dateFormatter = (format: string, date?: string) => {
  return dayjs(date).locale('ru').format(format);
};

export const fileSizeFormatter = (size: number = DEFAULT_VALUE_0) => {
  const sizeMB = size / BYTE_TO_MEGABYTE;
  const sizeKB = size / BYTE_TO_KILOBYTE;

  return sizeMB < DEFAULT_VALUE_1 ? `${Math.round(sizeKB)} KB` : `${sizeMB.toFixed(1)} MB`;
};
